import {
  ACCESS_TOKEN,
  CODE_VERIFIER,
  EXPIRES_TIME,
  ID_TOKEN,
  NONCE,
  REFRESH_TOKEN,
  STATE,
  getCookieByKey,
  performRemoveCookies,
  removeCookie,
  setSecureCookie,
} from "@/lib/auth/cookieUtils";

declare global {
  interface Window {
    APP_CONFIG?: {
      PUBLIC_AUTH_CONNECT_URL?: string;
      PUBLIC_AUTH_CLIENT_ID?: string;
      PUBLIC_APIM_BASE_URL?: string;
      PUBLIC_API_SERVICE_SLUG?: string;
      PUBLIC_ALLOWED_ORIGINS?: string;
      PUBLIC_PATIENT_CONCIERGE_API_URL?: string;
    };
  }
}

export type TokenResult = {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
};

const AUTH_RETURN_PATH_KEY = "pc_auth_return_path";

/** Remember where to land after /callback (e.g. agent device setup on `/`). */
export function setAuthReturnPath(path: string): void {
  if (typeof window === "undefined") return;
  const trimmed = path.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return;
  sessionStorage.setItem(AUTH_RETURN_PATH_KEY, trimmed);
}

export function consumeAuthReturnPath(fallback = "/dashboard"): string {
  if (typeof window === "undefined") return fallback;
  const raw = sessionStorage.getItem(AUTH_RETURN_PATH_KEY)?.trim();
  sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return fallback;
  return raw;
}

function authConnectUrl(): string {
  return (
    window.APP_CONFIG?.PUBLIC_AUTH_CONNECT_URL ||
    "https://dev-identity.andor.app"
  );
}

function clientId(): string {
  return window.APP_CONFIG?.PUBLIC_AUTH_CLIENT_ID || "think.andor";
}

export function getAuthToken(): string | undefined {
  return getCookieByKey(ACCESS_TOKEN);
}

export async function login(): Promise<void> {
  const redirectUri = `${window.location.origin}/callback`;
  const scope = "public openid uirestapi offline_access";
  const responseType = "id_token token code";
  const codeVerifier = generateRandomString(64);
  setSecureCookie(CODE_VERIFIER, codeVerifier);

  const nonce = generateRandomString(32);
  const state = generateRandomString(32);
  setSecureCookie(NONCE, nonce);
  setSecureCookie(STATE, state);

  const codeChallenge = await generateCodeChallenge(codeVerifier);

  const url = `${authConnectUrl()}/connect/authorize?${new URLSearchParams({
    client_id: clientId(),
    redirect_uri: redirectUri,
    response_type: responseType,
    scope,
    nonce,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  }).toString()}`;

  window.location.href = url;
}

export function logout(postLogoutPath = "/dashboard"): void {
  const idToken = getCookieByKey(ID_TOKEN);
  const redirectUri = `${window.location.origin}${postLogoutPath}`;
  performRemoveCookies();

  window.location.href = idToken
    ? `${authConnectUrl()}/connect/endsession?${new URLSearchParams({
        id_token_hint: idToken,
        post_logout_redirect_uri: redirectUri,
      }).toString()}`
    : redirectUri;
}

/** Clear local session and send the user through OIDC login again. */
export function forceReLogin(): void {
  if (typeof window === "undefined") return;
  // Avoid stacking multiple redirects from parallel 401s.
  const flag = "__pc_relogin";
  if ((window as unknown as Record<string, boolean>)[flag]) return;
  (window as unknown as Record<string, boolean>)[flag] = true;
  performRemoveCookies();
  void login();
}

/** True when an access token cookie exists and JWT exp (if present) is still valid. */
export function hasUsableAccessToken(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = token.split(".")[1];
    if (!payload) return true;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: number };
    if (typeof json.exp !== "number") return true;
    // Small skew so we refresh/relogin before the API rejects.
    return json.exp * 1000 > Date.now() + 15_000;
  } catch {
    return true;
  }
}

export async function exchangeCodeForToken(code: string): Promise<TokenResult> {
  const redirectUri = `${window.location.origin}/callback`;
  const params = new URLSearchParams();
  params.append("grant_type", "authorization_code");
  params.append("client_id", clientId());
  params.append("code", code);
  params.append("redirect_uri", redirectUri);

  const codeVerifier = getCookieByKey(CODE_VERIFIER);
  if (codeVerifier) {
    params.append("code_verifier", codeVerifier);
    removeCookie(CODE_VERIFIER);
  }

  const response = await fetch(`${authConnectUrl()}/connect/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params.toString(),
  });
  return (await response.json()) as TokenResult;
}

/** When to refresh relative to token lifetime (never past-due for short tokens). */
function refreshAtMsFromLifetime(lifetimeMs: number, fromMs = Date.now()): number {
  // Skew: 20% of lifetime, capped at 5m, floored at 30s — so a 5m token refreshes ~1m early,
  // not immediately (the old fixed 5m skew caused a 5s retry loop).
  const skewMs = Math.min(
    5 * 60_000,
    Math.max(30_000, Math.floor(lifetimeMs * 0.2)),
  );
  return fromMs + Math.max(lifetimeMs - skewMs, 5_000);
}

function refreshAtMsFromAccessToken(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: number; iat?: number };
    if (typeof json.exp !== "number") return null;
    const expMs = json.exp * 1000;
    const issuedMs =
      typeof json.iat === "number" ? json.iat * 1000 : Date.now();
    const lifetimeMs = Math.max(expMs - issuedMs, 0);
    if (lifetimeMs <= 0) return null;
    return refreshAtMsFromLifetime(lifetimeMs, issuedMs);
  } catch {
    return null;
  }
}

export function processTokenResponse(result: TokenResult): void {
  if (result.error) {
    console.error("Token Error:", result.error);
    return;
  }
  if (result.access_token) setSecureCookie(ACCESS_TOKEN, result.access_token);
  if (result.id_token) setSecureCookie(ID_TOKEN, result.id_token);
  if (result.refresh_token) setSecureCookie(REFRESH_TOKEN, result.refresh_token);

  let refreshAt: number | null = null;
  if (result.expires_in && result.expires_in > 0) {
    refreshAt = refreshAtMsFromLifetime(result.expires_in * 1000);
  } else if (result.access_token) {
    refreshAt = refreshAtMsFromAccessToken(result.access_token);
  }
  if (refreshAt != null) {
    setSecureCookie(EXPIRES_TIME, String(refreshAt));
  }
}

/** Milliseconds until we should refresh (EXPIRES_TIME is already skewed early). */
export function msUntilTokenRefresh(): number | null {
  const raw = getCookieByKey(EXPIRES_TIME);
  if (!raw) return null;
  const at = Number(raw);
  if (!Number.isFinite(at)) return null;
  return at - Date.now();
}

export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getCookieByKey(REFRESH_TOKEN);
  if (!refreshToken) return false;

  const params = new URLSearchParams();
  params.append("grant_type", "refresh_token");
  params.append("client_id", clientId());
  params.append("refresh_token", refreshToken);

  try {
    const response = await fetch(`${authConnectUrl()}/connect/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
    });
    const result = (await response.json()) as TokenResult;
    if (!response.ok || result.error || !result.access_token) {
      console.warn("[auth] refresh_token failed", result.error || response.status);
      return false;
    }
    processTokenResponse(result);
    return true;
  } catch (err) {
    console.warn("[auth] refresh_token threw", err);
    return false;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/** Deduped refresh — safe to call from parallel API 401 handlers. */
export function refreshAccessTokenShared(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Best-effort display claims from the access token (no signature verify). */
export function readTokenClaims(
  token?: string,
): { email?: string; name?: string; sub?: string } {
  const raw = token ?? getAuthToken();
  if (!raw) return {};
  try {
    const payload = raw.split(".")[1];
    if (!payload) return {};
    const json = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    return {
      email:
        typeof json.email === "string"
          ? json.email
          : typeof json.preferred_username === "string"
            ? json.preferred_username
            : undefined,
      name: typeof json.name === "string" ? json.name : undefined,
      sub: typeof json.sub === "string" ? json.sub : undefined,
    };
  } catch {
    return {};
  }
}

function generateRandomString(length: number): string {
  const charset =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const values = new Uint8Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (v) => charset[v % charset.length]).join("");
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
